import { generateId } from './contentUtils';
import {
  dedupeSourcesByReference,
  formatReference,
  mapCitationToSourceMeta,
  sortSourceMetas
} from '../lib/apaSlideFormatter.js';

const isReferenceSection = (section = {}) => {
  const title = (section.title || '').toLowerCase();
  return section.id === 'resources' || title.includes('resources') || title.includes('materials');
};

export const hasReferenceListBlock = (sections = []) =>
  (sections || []).some((section) =>
    (section?.blocks || []).some((block) => block?.type === 'apa-reference-list')
  );

const isSlideBreak = (block = {}) =>
  block?.type === 'divider' && block?.content?.isSlideBreak === true;

// A slide-break divider so the reference list is pushed onto its own slide
// rather than sharing one with existing content.
const createSlideBreak = () => ({
  id: generateId(),
  type: 'divider',
  content: {
    style: 'solid',
    thickness: 2,
    color: '#e5e7eb',
    spacing: 'medium',
    width: 'full',
    isSlideBreak: true
  }
});

// The reference list always belongs on its own slide. If the section it lands
// in already holds real content (anything other than trailing slide breaks),
// prefix a slide break so the reference list never shares a slide — including
// the single-slide "empty deck" case, where its lone placeholder block still
// counts as content.
const sectionHasContentBeforeReferenceList = (section = {}) =>
  (section.blocks || []).some((block) => !isSlideBreak(block));

// Full-slide frame (percent of the content region) so the reference list
// fills its slide by default in the 16:9 designer / PowerPoint export.
const FULL_SLIDE_FRAME = { x: 4, y: 4, w: 92, h: 92 };

// ---------------------------------------------------------------------------
// Pagination
//
// A reference slide holds a fixed amount of text. Long decks produce more
// references than fit, so the list is split into pages and each page gets its
// own slide. The split is computed from the rendered reference strings rather
// than a flat item count, because a reference that wraps to three lines costs
// three lines of slide space.
// ---------------------------------------------------------------------------

// Tuned against the 16:9 reference slide at its default type size: roughly
// this many characters fit on one line, and this many lines fit on one slide
// below the "References" heading. Erring low on the character count would
// break pages early and leave half-empty reference slides.
export const REFERENCE_CHARS_PER_LINE = 130;
export const REFERENCE_LINES_PER_SLIDE = 14;

export const estimateReferenceLines = (text, charsPerLine = REFERENCE_CHARS_PER_LINE) => {
  const length = typeof text === 'string' ? text.trim().length : 0;
  if (!length) return 1;
  return Math.max(1, Math.ceil(length / charsPerLine));
};

/**
 * Split already-deduped, already-sorted source metas into per-slide pages.
 * Always returns at least one (possibly empty) page so callers can index it
 * without a guard.
 */
export const paginateReferenceMetas = (metas = [], options = {}) => {
  const linesPerPage = options.linesPerPage || REFERENCE_LINES_PER_SLIDE;
  const charsPerLine = options.charsPerLine || REFERENCE_CHARS_PER_LINE;

  const pages = [];
  let current = [];
  let currentLines = 0;

  (metas || []).forEach((meta) => {
    const cost = estimateReferenceLines(formatReference(meta), charsPerLine);
    // A single reference longer than a whole slide still gets its own page
    // rather than being dropped or splitting mid-entry.
    if (current.length > 0 && currentLines + cost > linesPerPage) {
      pages.push(current);
      current = [];
      currentLines = 0;
    }
    current.push(meta);
    currentLines += cost;
  });

  if (current.length > 0 || pages.length === 0) pages.push(current);
  return pages;
};

/**
 * Resolve what one reference-list block should show: its slice of the deck's
 * full reference list, plus where that slice sits in the run of reference
 * slides. Shared by the app renderer, the HTML export and the PPTX export so
 * all three paginate identically.
 *
 * `block.autoCitations` carries the deck-wide citations; the block's own
 * `citations` are merged in and deduped against them.
 */
export const resolveReferencePage = (block = {}) => {
  const manual = Array.isArray(block.citations)
    ? block.citations
    : Array.isArray(block.references)
      ? block.references
      : [];
  const metas = sortSourceMetas(dedupeSourcesByReference([
    ...manual.map((citation) => mapCitationToSourceMeta(citation)),
    ...(block.autoCitations || [])
  ]));
  const pages = paginateReferenceMetas(metas);
  const pageIndex = Math.max(0, Number(block.referencePageIndex) || 0);
  return {
    metas: pages[pageIndex] || [],
    pageIndex,
    pageCount: pages.length,
    isContinuation: pageIndex > 0
  };
};

const referenceListBlocks = (sections = []) => {
  const found = [];
  (sections || []).forEach((section, sectionIndex) => {
    (section?.blocks || []).forEach((block, blockIndex) => {
      if (block?.type === 'apa-reference-list') {
        found.push({ block, sectionIndex, blockIndex });
      }
    });
  });
  return found;
};

/**
 * Every source the reference list has to show: the citations typed into the
 * reference blocks themselves plus the citations collected from the rest of
 * the deck, deduped and sorted into APA order.
 */
export const collectReferenceListMetas = (sections = [], autoCitations = []) => {
  const manual = [];
  referenceListBlocks(sections).forEach(({ block }) => {
    const list = Array.isArray(block.citations)
      ? block.citations
      : Array.isArray(block.references)
        ? block.references
        : [];
    list.forEach((citation) => manual.push(mapCitationToSourceMeta(citation)));
  });
  return sortSourceMetas(dedupeSourcesByReference([...manual, ...(autoCitations || [])]));
};

// Fold citations rescued from a removed page into the first page, skipping any
// the first page already carries.
const mergeCitations = (existing, incoming) => {
  const base = Array.isArray(existing) ? existing : [];
  const identity = (citation) => citation?.citationId
    || citation?.id
    || formatReference(mapCitationToSourceMeta(citation || {}))
    || '';
  const seen = new Set(base.map(identity).filter(Boolean));
  const additions = incoming.filter((citation) => {
    const key = identity(citation);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return additions.length ? [...base, ...additions] : base;
};

const createReferencePageBlock = (pageIndex) => ({
  id: generateId(),
  type: 'apa-reference-list',
  citations: [],
  referencePageIndex: pageIndex,
  frame: { ...FULL_SLIDE_FRAME }
});

/**
 * Keep the number of reference-list blocks in step with how many slides the
 * reference list actually needs.
 *
 * Continuation blocks are ordinary blocks in the deck — each preceded by a
 * slide break — so slides, thumbnails, the sections panel and every exporter
 * see them without special cases. Each block stores only its page index; the
 * page's contents are derived at render time from the deck's live citation
 * list, so the pages restack automatically as citations come and go.
 *
 * Returns the original array when nothing needs to change, so it is safe to
 * call from a React effect.
 */
export const syncReferenceListPages = (sections = [], autoCitations = []) => {
  const existing = referenceListBlocks(sections);
  if (!existing.length) return sections;

  const pageCount = paginateReferenceMetas(collectReferenceListMetas(sections, autoCitations)).length;

  const indexed = existing.map((entry, order) => ({ ...entry, order }));
  const surplus = indexed.slice(pageCount);
  const pageIndexNeedsUpdate = indexed
    .slice(0, pageCount)
    .some(({ block, order }) => (block.referencePageIndex || 0) !== order);

  if (!surplus.length && !pageIndexNeedsUpdate && existing.length === pageCount) {
    return sections;
  }

  // Drop the surplus blocks (and the slide break that introduced each one),
  // then renumber what is left. Anything the author typed into a removed page
  // moves to the first page rather than disappearing with the block.
  const surplusIds = new Set(surplus.map(({ block }) => block.id));
  const rescuedCitations = surplus.flatMap(({ block }) => (
    Array.isArray(block.citations)
      ? block.citations
      : Array.isArray(block.references) ? block.references : []
  ));
  let nextSections = sections.map((section) => {
    if (!(section?.blocks || []).some((block) => surplusIds.has(block?.id))) return section;
    const blocks = [];
    (section.blocks || []).forEach((block) => {
      if (surplusIds.has(block?.id)) {
        // Remove the slide break that put this continuation on its own slide.
        if (isSlideBreak(blocks[blocks.length - 1])) blocks.pop();
        return;
      }
      blocks.push(block);
    });
    return { ...section, blocks };
  });

  let order = 0;
  nextSections = nextSections.map((section) => {
    if (!(section?.blocks || []).some((block) => block?.type === 'apa-reference-list')) return section;
    return {
      ...section,
      blocks: (section.blocks || []).map((block) => {
        if (block?.type !== 'apa-reference-list') return block;
        const pageIndex = order;
        order += 1;
        const citations = pageIndex === 0 && rescuedCitations.length
          ? mergeCitations(block.citations, rescuedCitations)
          : block.citations;
        if (block.referencePageIndex === pageIndex && citations === block.citations) return block;
        return { ...block, referencePageIndex: pageIndex, citations };
      })
    };
  });

  // Add the pages that are still missing after the last existing block.
  const missing = pageCount - Math.min(existing.length, pageCount);
  if (missing > 0) {
    const last = existing[existing.length - 1];
    const additions = [];
    for (let page = existing.length; page < pageCount; page += 1) {
      additions.push(createSlideBreak(), createReferencePageBlock(page));
    }
    nextSections = nextSections.map((section, sectionIndex) => {
      if (sectionIndex !== last.sectionIndex) return section;
      const blocks = [...(section.blocks || [])];
      const anchor = blocks.findIndex((block) => block?.id === last.block.id);
      const insertAt = anchor === -1 ? blocks.length : anchor + 1;
      blocks.splice(insertAt, 0, ...additions);
      return { ...section, blocks };
    });
  }

  return nextSections;
};

export const ensureReferenceListBlockExists = (sections = []) => {
  if (hasReferenceListBlock(sections)) return sections;
  if (!sections || sections.length === 0) return sections;

  const referenceBlock = {
    id: generateId(),
    type: 'apa-reference-list',
    citations: [],
    referencePageIndex: 0,
    frame: { ...FULL_SLIDE_FRAME }
  };

  // Place the reference list at the end of the deck: prefer an explicit
  // resources/materials section, otherwise fall back to the last section.
  let targetIndex = sections.findIndex(isReferenceSection);
  if (targetIndex === -1) targetIndex = sections.length - 1;

  return sections.map((section, index) => {
    if (index !== targetIndex) return section;
    const blocks = section.blocks || [];
    const additions = sectionHasContentBeforeReferenceList(section)
      ? [createSlideBreak(), referenceBlock]
      : [referenceBlock];
    return { ...section, blocks: [...blocks, ...additions] };
  });
};
